import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/dark.css";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Pagination from '../components/Pagination';
import TemplatesList from './TemplatesList';
import { createPortal } from 'react-dom';

const colors = {
  1: { text: 'text-violet-300', bgSelected: 'bg-violet-500/10', borderSelected: 'border-violet-500', shadowSelected: 'shadow-violet-500/20' },
  2: { text: 'text-amber-300', bgSelected: 'bg-amber-500/10', borderSelected: 'border-amber-500', shadowSelected: 'shadow-amber-500/20' },
  3: { text: 'text-fuchsia-300', bgSelected: 'bg-fuchsia-500/10', borderSelected: 'border-fuchsia-500', shadowSelected: 'shadow-fuchsia-500/20' },
  4: { text: 'text-emerald-300', bgSelected: 'bg-emerald-500/10', borderSelected: 'border-emerald-500', shadowSelected: 'shadow-emerald-500/20' }
};

const selectStyles = {
  indicatorSeparator: () => ({ display: 'none' }),
  control: (base, state) => ({
    ...base,
    backgroundColor: '#1e293b',
    borderColor: state.isFocused ? '#3b82f6' : '#475569',
    boxShadow: 'none',
    '&:hover': { borderColor: '#3b82f6' },
  }),
  menuPortal: base => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#1e293b',
    border: '1px solid #475569',
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#334155' : 'transparent',
    color: '#e2e8f0',
    '&:active': { backgroundColor: '#475569' },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#334155',
    border: '1px solid #475569',
    borderRadius: '4px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#e2e8f0',
    fontSize: '11px'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#94a3b8',
    '&:hover': { backgroundColor: '#ef4444', color: 'white' },
  }),
  singleValue: (base) => ({
    ...base,
    color: '#e2e8f0',
  }),
};

const FieldWithAdd = ({ value, onChange, placeholder, type }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [status, setStatus] = useState('active');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (type) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/reference-values?type=${type}`)
        .then(res => setOptions(res.data.filter(item => item.status !== 'inactive').map(item => item.value)))
        .catch(err => console.error(err));
    }
  }, [type]);

  const handleSave = async () => {
    if (newValue.trim()) {
      if (type) {
        try {
          await axios.post(`${process.env.REACT_APP_API_URL}/api/reference-values`, { type, value: newValue.trim(), status });
          if (status !== 'inactive') {
            setOptions(prev => prev.includes(newValue.trim()) ? prev : [...prev, newValue.trim()]);
          }
        } catch (error) {
          console.error('Error saving reference value:', error);
        }
      }
      onChange({ target: { value: newValue.trim() } });
      setNewValue('');
      setStatus('active');
      setIsModalOpen(false);
    }
  };

  return (
    <div className="flex w-full relative">
      <select
        className="bg-slate-950 border border-slate-800 border-r-0 p-2 rounded-l-xl text-white text-sm w-full outline-none focus:border-emerald-500 appearance-none"
        value={value || ''}
        onChange={onChange}
      >
        <option value="" disabled hidden>Select {placeholder}</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt}>{opt}</option>
        ))}
        {value && !options.includes(value) && <option value={value}>{value}</option>}
      </select>
      <div className="absolute right-[45px] top-[10px] pointer-events-none">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="bg-emerald-500 hover:bg-emerald-400 text-white px-3 rounded-r-xl flex items-center justify-center font-bold text-xl transition-colors z-10"
        title={`Add new ${placeholder}`}
      >
        +
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">Add New {placeholder}</h3>
            <div className="mb-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Name</label>
                <input
                  type="text"
                  autoFocus
                  className="bg-slate-950 border border-slate-700 p-3 rounded-xl text-white w-full outline-none focus:border-emerald-500"
                  placeholder={`Enter ${placeholder}`}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Status</label>
                <select
                  className="bg-slate-950 border border-slate-700 p-3 rounded-xl text-white w-full outline-none focus:border-emerald-500 appearance-none"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl font-bold text-sm transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ClientMaster = () => {
  const [clients, setClients] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [hasPortalAccess, setHasPortalAccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [templates, setTemplates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // clientId of open menu
  const [removeTemplateModal, setRemoveTemplateModal] = useState(null); // client object
  const actionMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(null);
      }
    };
    if (actionMenuOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuOpen]);

  // Template States
  const [selectedClientForTemplate, setSelectedClientForTemplate] = useState(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [templateSections, setTemplateSections] = useState([]);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [deleteTemplateRowIndex, setDeleteTemplateRowIndex] = useState(null);

  // 4-Pane State
  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState(new Set());

  const handleTemplateCreated = (newTemplateId, newTemplateName) => {
    setIsTemplateModalOpen(false);

    // Update the local templates array so the newly created template shows in the dropdown
    setTemplates(prev => {
      const exists = prev.find(t => t.id === newTemplateId);
      if (exists) {
        return prev.map(t => t.id === newTemplateId ? { ...t, name: newTemplateName || t.name } : t);
      }
      return [...prev, { id: newTemplateId, name: newTemplateName }];
    });

    setFormData(prev => ({ 
      ...prev, 
      template_ids: [...(prev.template_ids || []), newTemplateId] 
    }));
  };

  // Form State
  const [formData, setFormData] = useState({
    client_data_collection_date_and_time: '',
    ref_data_source_id: '',
    ref_business_category_id: [],
    client_name: '',
    ref_client_type_id: '',
    ref_sub_agent_id: '',
    client_discount_perc: '',
    client_address_line1: '',
    client_address_line2: '',
    client_address_line3: '',
    ref_country_id: '',
    ref_state_id: '',
    ref_district_id: '',
    client_pincode: '',
    client_referred_date: '',
    client_referred_by: '',
    client_gst_no: '',
    user_name: '',
    password: '',
    status: 'Active',
    template_ids: []
  });

  const [contacts, setContacts] = useState([
    { contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }
  ]);

  const [emails, setEmails] = useState([
    { email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }
  ]);
  const [countriesList, setCountriesList] = useState([]);

  useEffect(() => {
    if (view === 'list') {
      fetchClients();
    }
    axios.get(`${process.env.REACT_APP_API_URL}/api/countries`)
      .then(res => setCountriesList(res.data))
      .catch(err => console.error(err));
    axios.get(`${process.env.REACT_APP_API_URL}/api/templates`)
      .then(res => setTemplates(res.data))
      .catch(err => console.error(err));
    axios.get(`${process.env.REACT_APP_API_URL}/api/categories`)
      .then(res => setDbCategories(res.data))
      .catch(err => console.error(err));
  }, [view]);

  const handleTemplateChange = async (selectedOptions) => {
    const ids = selectedOptions ? selectedOptions.map(o => o.id) : [];
    setSelectedTemplateIds(ids);

    if (ids.length > 0) {
      try {
        const responses = await Promise.all(ids.map(id => axios.get(process.env.REACT_APP_API_URL + `/api/templates/${id}/sections`, { withCredentials: true })));
        
        const initSelected = new Set();
        let firstSec = null;

        responses.forEach(res => {
          const secList = res.data;
          if (!firstSec && secList.length > 0) firstSec = secList[0];
          
          secList.forEach(s => {
            if (s.sub_category_id) initSelected.add(Number(s.sub_category_id));
            let items = [];
            if (typeof s.items === 'string') items = JSON.parse(s.items);
            else if (Array.isArray(s.items)) items = s.items;
            items.forEach(itemId => initSelected.add(Number(itemId)));
          });
        });

        setSelectedNodes(initSelected);

        if (firstSec) {
          if (firstSec.main_section_id) setSelectedL1(Number(firstSec.main_section_id));
          if (firstSec.category_id) setSelectedL2(Number(firstSec.category_id));
          if (firstSec.sub_category_id) setSelectedL3(Number(firstSec.sub_category_id));
        } else {
          setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
        }
        setIsCustomizing(false);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load template sections');
      }
    } else {
      setTemplateSections([]);
      setSelectedNodes(new Set());
      setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
    }
  };

  const handleTemplateEdit = async (client) => {
    setSelectedClientForTemplate(client);
    setIsCustomizing(false);
    setActionMenuOpen(null);
    setView('template');

    let existingIds = [];
    try {
      existingIds = typeof client.template_ids === 'string' ? JSON.parse(client.template_ids) : (client.template_ids || []);
    } catch (e) {}

    const masterIds = existingIds.filter(id => templates.some(t => Number(t.id) === Number(id)));
    setSelectedTemplateIds(masterIds.map(Number));

    if (existingIds.length > 0) {
      try {
        const responses = await Promise.all(existingIds.map(id => axios.get(process.env.REACT_APP_API_URL + `/api/templates/${id}/sections`, { withCredentials: true })));
        
        const initSelected = new Set();
        let firstSec = null;

        responses.forEach(res => {
          const secList = res.data;
          if (!firstSec && secList.length > 0) firstSec = secList[0];
          
          secList.forEach(s => {
            if (s.sub_category_id) initSelected.add(Number(s.sub_category_id));
            let items = [];
            if (typeof s.items === 'string') items = JSON.parse(s.items);
            else if (Array.isArray(s.items)) items = s.items;
            items.forEach(itemId => initSelected.add(Number(itemId)));
          });
        });

        setSelectedNodes(initSelected);

        if (firstSec) {
          if (firstSec.main_section_id) setSelectedL1(Number(firstSec.main_section_id));
          if (firstSec.category_id) setSelectedL2(Number(firstSec.category_id));
          if (firstSec.sub_category_id) setSelectedL3(Number(firstSec.sub_category_id));
        } else {
          setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load existing template data');
      }
    } else {
      setTemplateSections([]);
      setSelectedNodes(new Set());
      setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
    }
  };

  const handleTemplateSave = async () => {
    if (!isCustomizing && selectedTemplateIds.length === 0) {
      return toast.error('Please select a template or customize one.');
    }

    setSaving(true);
    try {
      if (isCustomizing) {
        const selectedL3s = new Set();
        const selectedL4sByL3 = {};

        selectedNodes.forEach(id => {
          const cat = dbCategories.find(c => c.id === id);
          if (!cat) return;
          if (cat.level === 3) {
            selectedL3s.add(id);
          } else if (cat.level === 4) {
            if (!selectedL4sByL3[cat.parent_id]) selectedL4sByL3[cat.parent_id] = [];
            selectedL4sByL3[cat.parent_id].push(id);
            selectedL3s.add(cat.parent_id); 
          }
        });

        const payloadSections = Array.from(selectedL3s).map(l3Id => {
          const l3 = dbCategories.find(c => c.id === l3Id);
          const l2 = dbCategories.find(c => c.id === l3?.parent_id);
          const l1 = dbCategories.find(c => c.id === l2?.parent_id);
          return {
            main_section_id: l1?.id || null,
            category_id: l2?.id || null,
            sub_category_id: l3?.id || null,
            ref_id: l3?.id || null,
            items: selectedL4sByL3[l3Id] || []
          };
        });

        await axios.post(
          process.env.REACT_APP_API_URL + `/api/clients/${selectedClientForTemplate.id}/custom-template`,
          {
            name: `${selectedClientForTemplate.client_name} - Custom Template`,
            description: `Customized financial template for ${selectedClientForTemplate.client_name}`,
            sections: payloadSections,
            master_template_ids: selectedTemplateIds
          },
          { withCredentials: true }
        );
        toast.success('Custom template saved and assigned!');
      } else {
        if (selectedTemplateIds.length === 0) {
          toast.error('Please select at least one template to assign');
          setSaving(false);
          return;
        }

        let currentIds = [];
        try { currentIds = typeof selectedClientForTemplate.template_ids === 'string' ? JSON.parse(selectedClientForTemplate.template_ids) : (selectedClientForTemplate.template_ids || []); } catch(e) {}
        
        const newIds = selectedTemplateIds.map(Number);
        const changed = currentIds.length !== newIds.length || newIds.some(id => !currentIds.includes(id)) || currentIds.some(id => !newIds.includes(id));

        if (changed) {
          await axios.patch(
            process.env.REACT_APP_API_URL + `/api/clients/${selectedClientForTemplate.id}/template`,
            { template_ids: newIds },
            { withCredentials: true }
          );
        }
        toast.success('Master template assigned successfully!');
      }
      setView('list');
    } catch (e) {
      console.error(e);
      toast.error('Error saving template assignment');
    } finally {
      setSaving(false);
    }
  };

  const addTemplateSection = () => {
    setTemplateSections([...templateSections, { main_section_id: '', category_id: '', sub_category_id: '', ref_id: '', items: [] }]);
  };

  const updateTemplateSection = (index, field, value) => {
    const newSections = [...templateSections];
    newSections[index][field] = value ? Number(value) : '';

    if (field === 'main_section_id') {
      newSections[index]['category_id'] = '';
      newSections[index]['sub_category_id'] = '';
      newSections[index]['ref_id'] = '';
      newSections[index]['items'] = [];
    } else if (field === 'category_id') {
      newSections[index]['sub_category_id'] = '';
      newSections[index]['ref_id'] = '';
      newSections[index]['items'] = [];
    } else if (field === 'sub_category_id') {
      newSections[index]['ref_id'] = value ? Number(value) : '';
      newSections[index]['items'] = [];
    }
    setTemplateSections(newSections);
  };

  const removeTemplateSection = (index) => {
    setDeleteTemplateRowIndex(index);
  };

  const confirmRemoveTemplateSection = () => {
    if (deleteTemplateRowIndex !== null) {
      const newSections = [...templateSections];
      newSections.splice(deleteTemplateRowIndex, 1);
      setTemplateSections(newSections);
      setDeleteTemplateRowIndex(null);
    }
  };

  // ── 4-Pane Builders Helper functions ────────────────────────────────────────

  const getDescendants = (nodeId, targetLevels) => {
    let result = [];
    const children = dbCategories.filter(c => c.parent_id === nodeId);
    children.forEach(c => {
      if (targetLevels.includes(c.level)) result.push(c.id);
      result = result.concat(getDescendants(c.id, targetLevels));
    });
    return result;
  };

  const isNodeFullySelected = (node) => {
    if (node.level === 4) return selectedNodes.has(node.id);
    if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (childrenL4.length === 0) return selectedNodes.has(node.id);
      return childrenL4.every(c => selectedNodes.has(c.id));
    }
    const descendants = getDescendants(node.id, [3, 4]);
    if (descendants.length === 0) return false;
    return descendants.every(id => selectedNodes.has(id));
  };

  const isNodePartiallySelected = (node) => {
    if (node.level === 4) return false;
    if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (childrenL4.length === 0) return false;
      const selectedCount = childrenL4.filter(c => selectedNodes.has(c.id)).length;
      return selectedCount > 0 && selectedCount < childrenL4.length;
    }
    const descendants = getDescendants(node.id, [3, 4]);
    if (descendants.length === 0) return false;
    const selectedCount = descendants.filter(id => selectedNodes.has(id)).length;
    return selectedCount > 0 && selectedCount < descendants.length;
  };

  const handleToggleNode = (node) => {
    if (!isCustomizing) return;
    const newSelected = new Set(selectedNodes);
    const fullySelected = isNodeFullySelected(node);

    if (node.level === 4) {
      if (fullySelected) newSelected.delete(node.id);
      else newSelected.add(node.id);
    } 
    else if (node.level === 3) {
      const childrenL4 = dbCategories.filter(c => c.parent_id === node.id);
      if (fullySelected) {
        newSelected.delete(node.id);
        childrenL4.forEach(c => newSelected.delete(c.id));
      } else {
        newSelected.add(node.id);
        childrenL4.forEach(c => newSelected.add(c.id));
      }
    }
    else {
      const descendants = getDescendants(node.id, [3, 4]);
      if (fullySelected) {
        descendants.forEach(id => newSelected.delete(id));
      } else {
        descendants.forEach(id => newSelected.add(id));
      }
    }
    setSelectedNodes(newSelected);
  };

  const renderPane = (level, title, items, selectedId, onSelect, parentId) => {
    const c = colors[level];
    
    return (
      <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-[calc(100vh-250px)] min-h-[450px]">
        <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex justify-between items-center">
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h3>
            <p className="text-xs text-slate-400 mt-1">{items.length} items</p>
          </div>
          {isCustomizing && level === 4 && items.length > 0 && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const allSelected = items.every(i => selectedNodes.has(i.id));
                const newSelected = new Set(selectedNodes);
                if (allSelected) {
                  items.forEach(i => newSelected.delete(i.id));
                } else {
                  items.forEach(i => newSelected.add(i.id));
                }
                setSelectedNodes(newSelected);
              }}
              className="text-[10px] font-bold px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 transition uppercase tracking-wider"
            >
              {items.every(i => selectedNodes.has(i.id)) ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
          {items.length === 0 ? (
             <div className="text-center py-10 text-slate-500 text-xs italic">No items available</div>
          ) : items.map(item => {
            const isSelected = selectedId === item.id;
            const isFullyChecked = isNodeFullySelected(item);
            const isPartiallyChecked = isNodePartiallySelected(item);
            
            return (
              <div 
                key={item.id} 
                onClick={() => onSelect && onSelect(item.id)} 
                className={`cursor-pointer p-3.5 rounded-xl border transition-all select-none group flex items-center justify-between gap-2 shadow-sm ${isSelected ? `${c.bgSelected} ${c.borderSelected} ${c.shadowSelected}` : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
              >
                <div className="flex items-center gap-3 overflow-hidden flex-1">
                  <h4 className={`font-bold text-sm truncate ${isSelected ? c.text : 'text-slate-200'}`}>{item.name}</h4>
                </div>
                
                <div 
                  onClick={(e) => { e.stopPropagation(); handleToggleNode(item); }}
                  className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isFullyChecked 
                      ? 'bg-emerald-500 border-emerald-500 text-slate-900 shadow-[0_0_10px_rgba(16,185,129,0.3)]' 
                      : isPartiallyChecked 
                        ? 'bg-emerald-500/30 border-emerald-500 text-emerald-400' 
                        : 'bg-slate-950 border-slate-600 hover:border-emerald-500'
                  } ${!isCustomizing ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {isFullyChecked && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                  )}
                  {isPartiallyChecked && !isFullyChecked && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4"/></svg>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getMobileLengthByCode = (code) => {
    const country = countriesList.find(c => c.country_code === code);
    return country ? country.mobile_length : 10;
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, { withCredentials: true });
      const parsedClients = res.data.map(c => {
        let parsedIds = [];
        if (c.template_ids) {
          try {
            const parsed = typeof c.template_ids === 'string' ? JSON.parse(c.template_ids) : c.template_ids;
            parsedIds = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
          } catch (e) {
            parsedIds = [c.template_ids].filter(Boolean);
          }
        }
        return { ...c, template_ids: parsedIds };
      });
      setClients(parsedClients);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredClients = clients.filter(client => {
    const searchStr = searchTerm.toLowerCase();
    return (
      (client.client_name && client.client_name.toLowerCase().includes(searchStr)) ||
      (client.ref_data_source_id && client.ref_data_source_id.toLowerCase().includes(searchStr)) ||
      (client.ref_client_type_id && client.ref_client_type_id.toLowerCase().includes(searchStr)) ||
      (client.status && client.status.toLowerCase().includes(searchStr))
    );
  });

  const totalPages = Math.ceil(filteredClients.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredClients.slice(indexOfFirstRecord, indexOfLastRecord);

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredClients.map((c, index) => ({
      "S.No": index + 1,
      "Client Name": c.client_name,
      "Data Source": c.ref_data_source_id || '',
      "Client Type": c.ref_client_type_id || '',
      "Collection Date": c.client_data_collection_date_and_time || '',
      "Status": c.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, "Clients_Export.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Client Directory", 14, 15);
    const tableColumn = ["S.No", "Client Name", "Data Source", "Client Type", "Collection Date", "Status"];
    const tableRows = [];
    filteredClients.forEach((c, index) => {
      tableRows.push([
        index + 1,
        c.client_name,
        c.ref_data_source_id || '',
        c.ref_client_type_id || '',
        c.client_data_collection_date_and_time || '',
        c.status
      ]);
    });
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20
    });
    doc.save("Clients_Export.pdf");
  };


  const handleEdit = async (id) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients/${id}`, { withCredentials: true });
      const client = res.data;
      setEditId(id);
      setFormData({
        client_data_collection_date_and_time: client.client_data_collection_date_and_time || '',
        ref_data_source_id: client.ref_data_source_id || '',
        ref_business_category_id: JSON.parse(client.ref_business_category_id || '[]'),
        client_name: client.client_name || '',
        ref_client_type_id: client.ref_client_type_id || '',
        ref_sub_agent_id: client.ref_sub_agent_id || '',
        client_discount_perc: client.client_discount_perc || '',
        client_address_line1: client.client_address_line1 || '',
        client_address_line2: client.client_address_line2 || '',
        client_address_line3: client.client_address_line3 || '',
        ref_country_id: client.ref_country_id || '',
        ref_state_id: client.ref_state_id || '',
        ref_district_id: client.ref_district_id || '',
        client_pincode: client.client_pincode || '',
        client_referred_date: client.client_referred_date || '',
        client_referred_by: client.client_referred_by || '',
        client_gst_no: client.client_gst_no || '',
        user_name: client.user_name || '',
        password: client.password || '',
        status: client.status || 'Active',
        template_ids: (() => {
          try {
            let parsed = client.template_ids ? (Array.isArray(client.template_ids) ? client.template_ids : JSON.parse(client.template_ids)) : (client.template_id ? [client.template_id] : []);
            return Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
          } catch(e) { return [client.template_ids, client.template_id].filter(Boolean); }
        })()
      });
      setHasPortalAccess(!!(client.user_name || client.password));
      setContacts(client.contacts.length ? client.contacts.map(c => ({ ...c, primary_contact: !!c.primary_contact, contact_country_code: c.contact_country_code || '+91' })) : [{ contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
      setEmails(client.emails.length ? client.emails.map(e => ({ ...e, primary_contact: !!e.primary_contact })) : [{ email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
      setView('form');
    } catch (err) {
      console.error(err);
    }
  };

  const handleClone = async (id) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients/${id}`, { withCredentials: true });
      const client = res.data;
      setEditId(null);
      
      let parsedTemplateIds = [];
      try {
        let parsed = client.template_ids ? (Array.isArray(client.template_ids) ? client.template_ids : JSON.parse(client.template_ids)) : (client.template_id ? [client.template_id] : []);
        parsedTemplateIds = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
      } catch(e) { parsedTemplateIds = [client.template_ids, client.template_id].filter(Boolean); }

      setFormData({
        client_data_collection_date_and_time: client.client_data_collection_date_and_time || '',
        ref_data_source_id: client.ref_data_source_id || '',
        ref_business_category_id: JSON.parse(client.ref_business_category_id || '[]'),
        client_name: `${client.client_name || ''} - Copy`,
        ref_client_type_id: client.ref_client_type_id || '',
        ref_sub_agent_id: client.ref_sub_agent_id || '',
        client_discount_perc: client.client_discount_perc || '',
        client_address_line1: client.client_address_line1 || '',
        client_address_line2: client.client_address_line2 || '',
        client_address_line3: client.client_address_line3 || '',
        ref_country_id: client.ref_country_id || '',
        ref_state_id: client.ref_state_id || '',
        ref_district_id: client.ref_district_id || '',
        client_pincode: client.client_pincode || '',
        client_referred_date: client.client_referred_date || '',
        client_referred_by: client.client_referred_by || '',
        client_gst_no: client.client_gst_no || '',
        user_name: '',
        password: '',
        status: 'Active',
        template_ids: parsedTemplateIds
      });
      setHasPortalAccess(false);
      setContacts(client.contacts.length ? client.contacts.map(c => ({ ...c, primary_contact: !!c.primary_contact, contact_country_code: c.contact_country_code || '+91' })) : [{ contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
      setEmails(client.emails.length ? client.emails.map(e => ({ ...e, primary_contact: !!e.primary_contact })) : [{ email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
      setView('form');
    } catch (err) {
      console.error(err);
      toast.error('Error cloning client');
    }
  };

  const handleDelete = (id) => {
    setItemToDelete(id);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${itemToDelete}`, { withCredentials: true });
      fetchClients();
      toast.success('Client deleted successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Error deleting client.');
    } finally {
      setItemToDelete(null);
    }
  };



  const removeTemplate = (client) => {
    setActionMenuOpen(null);
    setRemoveTemplateModal(client);
  };

  const confirmRemoveTemplate = async () => {
    if (!removeTemplateModal) return;
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/clients/${removeTemplateModal.id}/template`, {
        template_ids: []
      }, { withCredentials: true });
      toast.success('Template removed from client');
      fetchClients();
    } catch (err) {
      console.error(err);
      toast.error('Error removing template');
    } finally {
      setRemoveTemplateModal(null);
    }
  };



  const resetForm = () => {
    setEditId(null);
    setFormErrors({});
    setFormData({
      client_data_collection_date_and_time: '', ref_data_source_id: '', ref_business_category_id: [], client_name: '', ref_client_type_id: '', ref_sub_agent_id: '', client_discount_perc: '', client_address_line1: '', client_address_line2: '', client_address_line3: '', ref_country_id: '', ref_state_id: '', ref_district_id: '', client_pincode: '', client_referred_date: '', client_referred_by: '', client_gst_no: '', user_name: '', password: '', status: 'Active', template_ids: []
    });
    setHasPortalAccess(false);
    setShowPassword(false);
    setContacts([{ contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
    setEmails([{ email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.client_name.trim()) errors.client_name = "Client Name is required";

    contacts.forEach((contact, index) => {
      const length = getMobileLengthByCode(contact.contact_country_code || '+91');
      const regex = new RegExp(`^\\d{${length}}$`);
      if (contact.contact_number && !regex.test(contact.contact_number)) {
        errors[`contact_${index}_number`] = `Mobile number must be exactly ${length} digits`;
      }
    });

    emails.forEach((email, index) => {
      if (email.email_id && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.email_id)) {
        errors[`email_${index}_id`] = "Invalid email format";
      }
    });

    if (formData.client_pincode && !/^\d{6}$/.test(formData.client_pincode)) {
      errors.client_pincode = "Pincode must be exactly 6 digits";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix the validation errors.');
      return;
    }
    setFormErrors({});

    try {
      const payload = { 
        ...formData, 
        contacts, 
        emails,
        template_ids: formData.template_ids || []
      };
      if (editId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/clients/${editId}`, payload, { withCredentials: true });
        toast.success('Client updated successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/clients`, payload, { withCredentials: true });
        toast.success('Client created successfully!');
      }
      setView('list');
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error('Error saving client. Please check the fields.');
    }
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...contacts];
    newContacts[index][field] = value;
    if (field === 'primary_contact' && value) {
      newContacts.forEach((c, i) => { if (i !== index) c.primary_contact = false; });
    }
    setContacts(newContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index, field, value) => {
    const newEmails = [...emails];
    newEmails[index][field] = value;
    if (field === 'primary_contact' && value) {
      newEmails.forEach((e, i) => { if (i !== index) e.primary_contact = false; });
    }
    setEmails(newEmails);
  };

  const addEmail = () => {
    setEmails([...emails, { email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
  };

  const removeEmail = (index) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  return (
    <Layout title="Client Master" subtitle="Manage all client details and business contacts">
      <Toaster position="top-right" />
      {view === 'list' ? (
        <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white text-lg">Client Directory</h3>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Search clients..."
                className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
              <button onClick={exportToExcel} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl font-bold text-sm transition-colors">Excel</button>
              <button onClick={exportToPDF} className="bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-xl font-bold text-sm transition-colors">PDF</button>
              <button onClick={() => { resetForm(); setView('form'); }} className="bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-2 rounded-xl font-bold text-sm transition-colors">Add Client</button>
            </div>
          </div>

          <div className="w-full">
            <table className="w-full text-left border-collapse text-sm text-gray-300">
              <thead>
                <tr className="border-b border-slate-800 text-gray-500 font-mono whitespace-nowrap">
                  <th className="pb-3 pr-4">S.No</th>
                  <th className="pb-3 pr-4">Client Name</th>
                  <th className="pb-3 pr-4">Data Source</th>
                  <th className="pb-3 pr-4">Client Type</th>
                  <th className="pb-3 pr-4">Collection Date</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Template Name</th>
                  <th className="pb-3 pr-4 text-center">Template Status</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {currentRecords.map((client, index) => (
                  <tr key={client.id} className="hover:bg-slate-800/20">
                    <td className="py-4 text-gray-400 pr-4">{indexOfFirstRecord + index + 1}</td>
                    <td className="py-4 font-semibold text-white pr-4">{client.client_name}</td>
                    <td className="py-4 pr-4">{client.ref_data_source_id || '-'}</td>
                    <td className="py-4 pr-4">{client.ref_client_type_id || '-'}</td>
                    <td className="py-4 pr-4">{client.client_data_collection_date_and_time || '-'}</td>
                    <td className="py-4 pr-4"><span className={`px-2 py-1 rounded-md text-xs ${client.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>{client.status}</span></td>
                    <td className="py-4 pr-4 text-slate-300 font-semibold text-xs">
                      {client.template_ids && client.template_ids.length > 0 
                        ? (() => {
                            let parsed = typeof client.template_ids === 'string' ? JSON.parse(client.template_ids) : client.template_ids;
                            return parsed.map(id => {
                              const found = templates.find(t => t.id === Number(id));
                              return found ? found.name : id;
                            }).join(', ');
                          })()
                        : '-'}
                    </td>
                    <td className="py-4 pr-4 text-center">
                      {client.template_id || (client.template_ids && client.template_ids.length > 0) ? (
                        <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-5 h-5 text-rose-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
                      )}
                    </td>
                    <td className="py-4 text-right min-w-[160px] relative">
                      <div className="flex justify-end gap-3 items-center">
                        <div className="relative" ref={actionMenuOpen === client.id ? actionMenuRef : null}>
                          <button
                            onClick={() => setActionMenuOpen(actionMenuOpen === client.id ? null : client.id)}
                            className="text-gray-400 hover:text-white transition-colors bg-slate-800 p-1.5 rounded"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
                          </button>
                          {actionMenuOpen === client.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-xl py-1 z-50 border border-slate-700">
                              {(client.template_ids && client.template_ids.length > 0) ? (
                                <>
                                  <button
                                    onClick={() => handleTemplateEdit(client)}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                                    Edit Template
                                  </button>
                                  <button
                                    onClick={() => removeTemplate(client)}
                                    className="w-full text-left px-4 py-2 text-sm text-rose-500 hover:bg-slate-700 hover:text-rose-400 transition-colors flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    Remove Template
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleTemplateEdit(client)}
                                  className="w-full text-left px-4 py-2 text-sm text-emerald-400 hover:bg-slate-700 hover:text-emerald-300 transition-colors flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                                  Add Template
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <button onClick={() => handleEdit(client.id)} title="Edit Client" className="text-blue-400 hover:text-blue-300 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                        </button>
                        <button onClick={() => handleClone(client.id)} title="Clone Client" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                        </button>
                        <button onClick={() => handleDelete(client.id)} title="Delete Client" className="text-rose-500 hover:text-rose-400 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {currentRecords.length === 0 && (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-500">No clients found matching your search.</td></tr>
                )}
              </tbody>
            </table>
            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredClients.length} />
          </div>
        </div>
      ) : view === 'form' ? (
        <div className="bg-slate-900/40 p-8 rounded-3xl w-full border border-slate-800 shadow-xl">
          <div className="flex items-center justify-between mb-8 border-b border-slate-800 pb-4">
            <h3 className="text-2xl font-bold text-white">{editId ? 'Edit Client' : 'Add New Client'}</h3>
            <button onClick={() => setView('list')} className="text-gray-400 hover:text-white transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-8" noValidate>

            {/* Basic Details */}
            <div>
              <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2">Basic Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input type="text" placeholder="Client Name *" required className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full" value={formData.client_name} onChange={e => { setFormData({ ...formData, client_name: e.target.value }); setFormErrors({ ...formErrors, client_name: null }) }} />
                  {formErrors.client_name && <p className="text-rose-500 text-xs mt-1">{formErrors.client_name}</p>}
                </div>
                <Flatpickr
                  options={{ enableTime: true, dateFormat: "Y-m-d\\TH:i" }}
                  placeholder="Collection Date & Time"
                  className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500"
                  value={formData.client_data_collection_date_and_time}
                  onChange={(dates, dateStr) => setFormData({ ...formData, client_data_collection_date_and_time: dateStr })}
                />
                <FieldWithAdd type="data_source" placeholder="Data Source" value={formData.ref_data_source_id} onChange={e => setFormData({ ...formData, ref_data_source_id: e.target.value })} />
                <FieldWithAdd type="business_category" placeholder="Business Category" value={formData.ref_business_category_id[0] || ''} onChange={e => setFormData({ ...formData, ref_business_category_id: [e.target.value] })} />
                <div className="flex w-full items-center gap-2">
                  <div className="flex-1">
                    <Select
                      isMulti
                      styles={selectStyles}
                      placeholder="Select Templates"
                      value={templates.filter(t => (formData.template_ids || []).some(id => Number(id) === Number(t.id)))}
                      onChange={(selectedOptions) => setFormData({ ...formData, template_ids: selectedOptions ? selectedOptions.map(opt => opt.id) : [] })}
                      options={templates}
                      getOptionLabel={(e) => e.name}
                      getOptionValue={(e) => e.id}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsTemplateModalOpen(true)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-3 py-2 rounded-xl flex items-center justify-center font-bold transition-colors shadow-lg whitespace-nowrap text-xs"
                    title="Add new template"
                  >
                    + Add New
                  </button>
                </div>
                <input type="text" placeholder="Client Type" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_client_type_id} onChange={e => setFormData({ ...formData, ref_client_type_id: e.target.value })} />
                <input type="text" placeholder="Sub Agent" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_sub_agent_id} onChange={e => setFormData({ ...formData, ref_sub_agent_id: e.target.value })} />
                <input type="number" placeholder="Discount %" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_discount_perc} onChange={e => setFormData({ ...formData, client_discount_perc: e.target.value })} />
                <Flatpickr
                  options={{ dateFormat: "Y-m-d" }}
                  placeholder="Referred Date"
                  className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500"
                  value={formData.client_referred_date}
                  onChange={(dates, dateStr) => setFormData({ ...formData, client_referred_date: dateStr })}
                />
                <input type="text" placeholder="Referred By" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_referred_by} onChange={e => setFormData({ ...formData, client_referred_by: e.target.value })} />
                <input type="text" placeholder="GST No" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_gst_no} onChange={e => setFormData({ ...formData, client_gst_no: e.target.value })} />
              </div>
            </div>

            {/* Address Details */}
            <div>
              <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2">Address Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="text" placeholder="Address Line 1" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_address_line1} onChange={e => setFormData({ ...formData, client_address_line1: e.target.value })} />
                <input type="text" placeholder="Address Line 2" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_address_line2} onChange={e => setFormData({ ...formData, client_address_line2: e.target.value })} />
                <input type="text" placeholder="Address Line 3" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.client_address_line3} onChange={e => setFormData({ ...formData, client_address_line3: e.target.value })} />
                <select
                  className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition"
                  value={formData.ref_country_id}
                  onChange={e => setFormData({ ...formData, ref_country_id: e.target.value })}
                >
                  <option value="" disabled hidden>Select Country</option>
                  {countriesList.map(c => <option key={c.id} value={c.country_name}>{c.country_name}</option>)}
                </select>
                <input type="text" placeholder="State" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_state_id} onChange={e => setFormData({ ...formData, ref_state_id: e.target.value })} />
                <input type="text" placeholder="District" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_district_id} onChange={e => setFormData({ ...formData, ref_district_id: e.target.value })} />
                <div>
                  <input
                    type="text"
                    placeholder="Pincode"
                    className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500"
                    value={formData.client_pincode}
                    onChange={e => {
                      setFormData({ ...formData, client_pincode: e.target.value.replace(/\D/g, '').slice(0, 6) });
                      setFormErrors(prev => ({ ...prev, client_pincode: null }));
                    }}
                  />
                  {formErrors.client_pincode && <p className="text-rose-500 text-xs mt-1 px-1">{formErrors.client_pincode}</p>}
                </div>
              </div>
            </div>

            {/* Contact Numbers */}
            <div>
              <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2 flex justify-between">
                <span>Contact Numbers</span>
                <button type="button" onClick={addContact} className="text-emerald-400 hover:text-emerald-300 text-xs">+ Add Contact</button>
              </h4>
              <div className="space-y-4">
                {contacts.map((contact, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                    <input type="text" placeholder="Contact Person" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.contact_person} onChange={e => updateContact(index, 'contact_person', e.target.value)} />
                    <FieldWithAdd type="designation" placeholder="Designation" value={contact.ref_designation_id} onChange={e => updateContact(index, 'ref_designation_id', e.target.value)} />
                    <input type="text" placeholder="Number Type" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.ref_contact_number_type_id} onChange={e => updateContact(index, 'ref_contact_number_type_id', e.target.value)} />
                    <div>
                      <div className="flex bg-slate-950 border border-slate-800 rounded-md overflow-hidden">
                        <select
                          className="bg-slate-800 text-gray-300 px-1 py-2 flex items-center justify-center text-xs border-r border-slate-700 outline-none w-[60px]"
                          value={contact.contact_country_code || '+91'}
                          onChange={e => updateContact(index, 'contact_country_code', e.target.value)}
                        >
                          {countriesList.map(c => <option key={c.id} value={c.country_code}>{c.country_code}</option>)}
                        </select>
                        <input type="text" placeholder="Contact Number" className="bg-transparent p-2 text-white text-xs w-full outline-none" value={contact.contact_number} onChange={e => { updateContact(index, 'contact_number', e.target.value.replace(/\D/g, '').slice(0, getMobileLengthByCode(contact.contact_country_code || '+91'))); setFormErrors(prev => ({ ...prev, [`contact_${index}_number`]: null })) }} />
                      </div>
                      {formErrors[`contact_${index}_number`] && <p className="text-rose-500 text-xs mt-1">{formErrors[`contact_${index}_number`]}</p>}
                    </div>
                    <input type="text" placeholder="Extension" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.contact_extension} onChange={e => updateContact(index, 'contact_extension', e.target.value)} />
                    <Flatpickr
                      options={{ enableTime: true, noCalendar: true, dateFormat: "H:i", time_24hr: true }}
                      placeholder="Timing From"
                      className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full outline-none focus:border-emerald-500"
                      value={contact.contact_timing_from}
                      onChange={(dates, dateStr) => updateContact(index, 'contact_timing_from', dateStr)}
                    />
                    <Flatpickr
                      options={{ enableTime: true, noCalendar: true, dateFormat: "H:i", time_24hr: true }}
                      placeholder="Timing To"
                      className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full outline-none focus:border-emerald-500"
                      value={contact.contact_timing_to}
                      onChange={(dates, dateStr) => updateContact(index, 'contact_timing_to', dateStr)}
                    />
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <input type="checkbox" checked={contact.primary_contact} onChange={e => updateContact(index, 'primary_contact', e.target.checked)} />
                      Primary
                      {contacts.length > 1 && (
                        <button type="button" onClick={() => removeContact(index)} className="ml-auto text-rose-500 hover:text-rose-400 font-bold">Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email IDs */}
            <div>
              <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2 flex justify-between">
                <span>Email IDs</span>
                <button type="button" onClick={addEmail} className="text-emerald-400 hover:text-emerald-300 text-xs">+ Add Email</button>
              </h4>
              <div className="space-y-4">
                {emails.map((email, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                    <div className="col-span-2">
                      <input type="email" placeholder="Email ID" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full" value={email.email_id} onChange={e => { updateEmail(index, 'email_id', e.target.value); setFormErrors(prev => ({ ...prev, [`email_${index}_id`]: null })) }} />
                      {formErrors[`email_${index}_id`] && <p className="text-rose-500 text-xs mt-1">{formErrors[`email_${index}_id`]}</p>}
                    </div>
                    <input type="text" placeholder="Contact Person" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={email.contact_person} onChange={e => updateEmail(index, 'contact_person', e.target.value)} />
                    <input type="text" placeholder="Designation" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={email.ref_designation_id} onChange={e => updateEmail(index, 'ref_designation_id', e.target.value)} />
                    <div className="flex items-center gap-2 text-xs text-gray-400 justify-end">
                      <input type="checkbox" checked={email.primary_contact} onChange={e => updateEmail(index, 'primary_contact', e.target.checked)} />
                      Primary
                      {emails.length > 1 && (
                        <button type="button" onClick={() => removeEmail(index)} className="ml-2 text-rose-500 hover:text-rose-400 font-bold">Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Login Access */}
            <div className="bg-slate-900/50 p-5 rounded-2xl border border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-emerald-500 font-bold">Client Portal Access</h4>
                  <p className="text-xs text-gray-400 mt-1">Enable portal access for this client to allow them to login.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={hasPortalAccess}
                    onChange={(e) => {
                      setHasPortalAccess(e.target.checked);
                      if (!e.target.checked) {
                        setFormData({ ...formData, user_name: '', password: '' });
                      }
                    }}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>

              {hasPortalAccess && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-800/50 animate-fade-in-down">
                  <input type="text" placeholder="Username" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500" value={formData.user_name} onChange={e => setFormData({ ...formData, user_name: e.target.value })} />
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full pr-10 outline-none focus:border-emerald-500"
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-emerald-400 focus:outline-none transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-6 border-t border-slate-800">
              <button type="button" onClick={() => setView('list')} className="w-1/3 bg-slate-800 text-white p-3 rounded-xl font-bold">Cancel</button>
              <button type="submit" className="w-2/3 bg-emerald-500 text-black font-bold p-3 rounded-xl hover:bg-emerald-400">Save Client</button>
            </div>
          </form>
        </div>
      ) : view === 'template' && selectedClientForTemplate ? (
        <div className="bg-slate-900/60 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-xl p-6 w-full mx-auto">

          <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
            <div className="w-full md:w-1/2">
              <Select
                isMulti
                styles={selectStyles}
                placeholder="Select Master Templates..."
                value={templates.filter(t => selectedTemplateIds.some(id => Number(id) === Number(t.id)))}
                onChange={handleTemplateChange}
                options={templates}
                getOptionLabel={(e) => e.name}
                getOptionValue={(e) => e.id}
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-blue-500 bg-slate-800 border-slate-600 rounded focus:ring-blue-500 focus:ring-2 accent-blue-500 cursor-pointer"
                  checked={isCustomizing}
                  onChange={(e) => setIsCustomizing(e.target.checked)}
                />
                <span className="font-bold text-slate-200">Customize Template</span>
              </label>
              {isCustomizing && (
                <span className="text-xs bg-[#241a0d] text-amber-500 px-3 py-1.5 rounded-md border border-amber-900/50">
                  Editing specifically for {selectedClientForTemplate.client_name}
                </span>
              )}
            </div>
          </div>

          {/* Dynamic 4-Pane Builder */}
          <div className="flex gap-4 overflow-x-auto pb-4 w-full custom-scrollbar items-start mt-6">
             {renderPane(1, 'LEVEL 1 (MAIN SECTION)', dbCategories.filter(c => c.level === 1).sort((a,b) => a.sort_order - b.sort_order), selectedL1, id => { setSelectedL1(id); setSelectedL2(null); setSelectedL3(null); }, null)}
             {selectedL1 && renderPane(2, 'LEVEL 2 (CATEGORY)', dbCategories.filter(c => c.level === 2 && c.parent_id === selectedL1).sort((a,b) => a.sort_order - b.sort_order), selectedL2, id => { setSelectedL2(id); setSelectedL3(null); }, selectedL1)}
             {selectedL2 && renderPane(3, 'LEVEL 3 (SUB CATEGORY)', dbCategories.filter(c => c.level === 3 && c.parent_id === selectedL2).sort((a,b) => a.sort_order - b.sort_order), selectedL3, id => { setSelectedL3(id); }, selectedL2)}
             {selectedL3 && renderPane(4, 'LEVEL 4 (ITEM)', dbCategories.filter(c => c.level === 4 && c.parent_id === selectedL3).sort((a,b) => a.sort_order - b.sort_order), null, null, selectedL3)}
          </div>

          <div className="flex gap-4 pt-6 border-t border-slate-800 mt-8">
            <button
              type="button"
              onClick={() => setView('list')}
              className="w-1/3 bg-slate-800 text-white p-3 rounded-xl font-bold hover:bg-slate-700 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleTemplateSave}
              disabled={saving}
              className="w-2/3 bg-emerald-500 text-black font-bold p-3 rounded-xl hover:bg-emerald-400 transition shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50"
            >
              Save Template
            </button>
          </div>
        </div>
      ) : null}

      {/* ── Delete Row Confirmation Modal ─────────────────────────────────────── */}
      {deleteTemplateRowIndex !== null && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Remove Row</h3>
              <p className="text-slate-400 text-sm mb-6">
                Are you sure you want to remove this row? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button 
                type="button"
                onClick={() => setDeleteTemplateRowIndex(null)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 rounded-xl transition"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={confirmRemoveTemplateSection}
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-2.5 rounded-xl transition shadow-[0_0_15px_rgba(225,29,72,0.4)]"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">Delete Client</h3>
            <p className="text-sm text-gray-400 mb-6">Are you sure you want to delete this client? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setItemToDelete(null)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-sm transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {removeTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Remove Template</h3>
              <p className="text-slate-400 text-sm mb-6">
                Are you sure you want to remove the template from <span className="font-bold text-slate-200">{removeTemplateModal.client_name}</span>?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRemoveTemplateModal(null)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-semibold py-2.5 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveTemplate}
                className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-2.5 rounded-xl transition shadow-[0_0_15px_rgba(225,29,72,0.4)]"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {isTemplateModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-8">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <button
              onClick={() => setIsTemplateModalOpen(false)}
              className="absolute top-6 right-6 bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white p-2 rounded-xl z-50 transition-colors"
              title="Close Modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="p-4 md:p-8">
              <TemplatesList 
                isEmbedded={true} 
                initialView="form" 
                onTemplateCreated={handleTemplateCreated} 
                onCancel={() => setIsTemplateModalOpen(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </Layout>
  );
};

export default ClientMaster;
